import { throttle } from '@scripts/misc/utils';
import Swiper from 'swiper';
import { Navigation, Autoplay, Pagination } from 'swiper/modules';
Swiper.use([Navigation, Autoplay, Pagination]);

export default function CarouselFront() {
  const carousels = document.querySelectorAll('.js-carousel');

  /**
   * Checks if more duplicate slides are needed for seamless infinite scrolling
   * and adds them if necessary. Handles both discrete slide changes and free scroll.
   */
  function checkAndAddDuplicates(swiper) {
    // Prevent infinite loops by checking if we're already updating
    if (swiper.el.isUpdatingDuplicates) {
      return;
    }

    const activeIndex = swiper.activeIndex || 0;
    const totalSlides = swiper.slides.length;
    const slidesPerView = Math.ceil(swiper.params.slidesPerView || 1);
    const originalCount = swiper.el.originalSlidesCount || totalSlides;
    const translate = Math.abs(swiper.translate || 0);
    const slideWidth = swiper.slidesSizesGrid[0] || 200;
    const currentSlidePosition = Math.floor(translate / slideWidth);

    // Calculate buffer needed for smooth infinite scrolling
    const bufferNeeded = slidesPerView * 3;
    const slidesAhead = totalSlides - Math.max(activeIndex, currentSlidePosition) - slidesPerView;
    const slidesBehind = Math.max(activeIndex, currentSlidePosition);

    // Check proximity to boundaries based on translate position
    const nearEnd = translate + slideWidth * slidesPerView * 2 >= totalSlides * slideWidth;
    const nearBeginning = currentSlidePosition < bufferNeeded;

    const needsMoreDuplicates =
      slidesAhead < bufferNeeded ||
      slidesBehind < bufferNeeded ||
      totalSlides < originalCount * 4 ||
      nearEnd ||
      nearBeginning;

    if (needsMoreDuplicates) {
      duplicateSlides(swiper);
    }
  }

  /**
   * Duplicates slides to enable seamless infinite scrolling when there aren't enough slides
   * to fill the viewport and provide smooth looping. This is a workaround for Swiper's
   * loop behavior with centered slides and multiple slides per view.
   * See: https://github.com/nolimits4web/swiper/issues/7254
   */
  function duplicateSlides(swiper) {
    const swiperEl = swiper.el;

    // Set flag to prevent infinite loops
    swiperEl.isUpdatingDuplicates = true;

    const slidesPerView = swiperEl.swiper.params.slidesPerView;
    const spaceBetween = swiperEl.swiper.params.spaceBetween || 0;

    // Store original dimensions on first run
    if (!swiperEl.originalSlidesWidth) {
      swiperEl.originalSlidesWidth = swiperEl.swiper.slidesSizesGrid.reduce(
        (accumulator, currentValue) => accumulator + currentValue,
        0,
      );
      swiperEl.originalSlidesCount = swiperEl.swiper.slides.length;
    }

    const allSlidesWidth = swiperEl.swiper.slidesSizesGrid.reduce(
      (accumulator, currentValue) => accumulator + currentValue,
      0,
    );
    const averageSlideWidth = allSlidesWidth / swiperEl.swiper.slides.length;

    // Calculate required width for seamless looping with buffer
    const bufferSlides = Math.ceil(slidesPerView) * 3;
    const totalNeededWidth = allSlidesWidth + averageSlideWidth * bufferSlides + spaceBetween;

    // Add duplicates if current slides don't provide enough width
    if (totalNeededWidth > allSlidesWidth) {
      const existingDuplicates = Math.round(
        (swiperEl.swiper.slides.length - swiperEl.originalSlidesCount) / swiperEl.originalSlidesCount,
      );

      const setsNeeded = Math.ceil(totalNeededWidth / swiperEl.originalSlidesWidth);
      const duplicatesNeeded = Math.max(0, setsNeeded - 1 - existingDuplicates);

      // Generate HTML for all original slides if not already done
      if (!swiperEl.allSlidesHTML) {
        swiperEl.allSlidesHTML = swiperEl.swiper.slides
          .slice(0, swiperEl.originalSlidesCount)
          .reduce((accumulator, currentValue) => {
            const dataAttributes = Array.from(currentValue.attributes)
              .filter((attr) => attr.name.startsWith('data-'))
              .map((attr) => `${attr.name}="${attr.value}"`)
              .join(' ');

            return `${accumulator}<div class="swiper-slide swiper-slide-duplicate" ${dataAttributes}>${currentValue.innerHTML}</div>`;
          }, '');
      }

      // Add duplicates both at the beginning and end for proper infinite loop
      const halfDuplicates = Math.ceil(duplicatesNeeded / 2);
      const remainingDuplicates = duplicatesNeeded - halfDuplicates;

      // Add duplicates at the beginning (for right-to-left scrolling)
      for (let i = 0; i < halfDuplicates; i++) {
        swiperEl.swiper.slidesEl.insertAdjacentHTML('afterbegin', swiperEl.allSlidesHTML);
      }

      // Add duplicates at the end (for left-to-right scrolling)
      for (let i = 0; i < remainingDuplicates; i++) {
        swiperEl.swiper.slidesEl.insertAdjacentHTML('beforeend', swiperEl.allSlidesHTML);
      }

      // Update swiper to reflect changes
      swiperEl.swiper.update();
    }

    // Clear flag after allowing events to settle
    setTimeout(() => {
      swiperEl.isUpdatingDuplicates = false;
    }, 100);
  }

  for (let carousel of carousels) {
    const slidesCount = carousel.querySelectorAll('.swiper-slide').length;

    // Get settings from data attributes
    const sliderType = carousel.getAttribute('data-slider-type') || 'carousel-with-text';
    const autoplay = JSON.parse(carousel.getAttribute('data-autoplay') || 'false');
    const autoplayDelay = parseInt(carousel.getAttribute('data-autoplay-delay') || '3000');
    const pauseOnHover = JSON.parse(carousel.getAttribute('data-pause-on-hover') || 'true');
    const touchSwipe = JSON.parse(carousel.getAttribute('data-touch-swipe') || 'true');
    const showNavigation = JSON.parse(carousel.getAttribute('data-show-navigation') || 'true');
    const itemsToShow = parseInt(carousel.getAttribute('data-items-to-show') || '3');
    const enableLoop = JSON.parse(carousel.getAttribute('data-enable-loop') || 'true');
    const centeredSlides = JSON.parse(carousel.getAttribute('data-centered-slides') || 'true');

    // Calculate slides per view based on slider type
    let slidesPerViewConfig;

    if (sliderType === 'images-carousel') {
      // For image carousel, use itemsToShow setting with whole numbers (no partial slide effect)
      const baseItems = itemsToShow || 1;
      slidesPerViewConfig = {
        mobile: Math.min(baseItems, 1),
        tablet: Math.min(baseItems, 2),
        desktop: Math.min(baseItems, slidesCount),
      };
    } else {
      // For carousel with text, use the proven working configuration with partial slide effect
      slidesPerViewConfig = {
        mobile: 1.2,
        tablet: 2.2,
        desktop: slidesCount > 3 ? 3.2 : 3,
      };
    }

    // The custom slide-duplication below is a workaround for Swiper's loop +
    // centered-slides + partial-slides combination used by the text variant.
    // The images variant uses whole-number slidesPerView, so Swiper's native
    // loop handles it cleanly — duplicating slides there just bloats DOM and
    // pagination bullets.
    const useCustomDuplication = enableLoop && sliderType !== 'images-carousel';

    // Build swiper configuration
    const swiperConfig = {
      slidesPerView: slidesPerViewConfig.mobile,
      // For images-carousel we advance one "page" at a time so each pagination
      // bullet maps to a group of visible slides instead of every single slide.
      slidesPerGroup: sliderType === 'images-carousel' ? slidesPerViewConfig.mobile : 1,
      // centeredSlides + slidesPerGroup > 1 produces a confusing offset effect,
      // so disable centering for the grouped images variant.
      centeredSlides: sliderType === 'images-carousel' ? false : centeredSlides,
      spaceBetween: 0,
      loop: enableLoop,
      loopAddBlankSlides: enableLoop && sliderType !== 'images-carousel',
      allowTouchMove: touchSwipe,
      simulateTouch: touchSwipe,
      breakpoints: {
        // when window width is >= 760px
        760: {
          slidesPerView: slidesPerViewConfig.tablet,
          slidesPerGroup: sliderType === 'images-carousel' ? slidesPerViewConfig.tablet : 1,
        },
        // when window width is >= 900px
        900: {
          slidesPerView: slidesPerViewConfig.desktop,
          slidesPerGroup: sliderType === 'images-carousel' ? slidesPerViewConfig.desktop : 1,
        },
      },
      on: {
        init: useCustomDuplication ? duplicateSlides : function () {},
        resize: useCustomDuplication ? throttle(duplicateSlides, 100) : function () {},
        slideChange: useCustomDuplication
          ? function (swiper) {
              checkAndAddDuplicates(swiper);
              // Call original slideChange if it exists
              if (typeof originalSlideChange === 'function') {
                originalSlideChange(swiper);
              }
            }
          : function () {},
        transitionEnd: useCustomDuplication
          ? function (swiper) {
              checkAndAddDuplicates(swiper);
            }
          : function () {},
        touchEnd: useCustomDuplication
          ? function (swiper) {
              checkAndAddDuplicates(swiper);
            }
          : function () {},
        setTranslate: useCustomDuplication
          ? throttle(function (swiper) {
              checkAndAddDuplicates(swiper);
            }, 500)
          : function () {},
      },
    };

    // Add navigation if enabled — scope element lookup to this carousel's wrapper
    // so multiple carousels on the same page don't share the first buttons in the DOM.
    if (showNavigation) {
      const scope = carousel.closest('.js-carousel-wrapper') || carousel.parentElement || document;
      const prevEl = scope.querySelector('.js-carousel-button-prev');
      const nextEl = scope.querySelector('.js-carousel-button-next');
      const paginationEl = scope.querySelector('.js-carousel-pagination');

      if (prevEl && nextEl) {
        swiperConfig.navigation = {
          nextEl,
          prevEl,
        };
      }

      if (paginationEl) {
        swiperConfig.pagination = {
          el: paginationEl,
          clickable: true,
        };
      }
    }

    // Add autoplay if enabled
    if (autoplay) {
      swiperConfig.autoplay = {
        delay: autoplayDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter: pauseOnHover,
      };
    }

    // Store original slideChange function for carousel-with-text type
    let originalSlideChange = null;
    if (sliderType !== 'images-carousel') {
      originalSlideChange = function (swiper) {
        let slide = swiper.slides[swiper.activeIndex];

        if (!slide) {
          return;
        }

        let slideTitle = slide.getAttribute('data-title') ?? '';
        let slideLinkLabel = slide.getAttribute('data-link-label') ?? null;
        let slideLinkUrl = slide.getAttribute('data-link-url') ?? null;

        let swiperEl = swiper.el;
        let titleEl = swiperEl.querySelector('.js-carousel-title');
        if (titleEl) {
          titleEl.innerHTML = slideTitle;
        }

        let linkEl = swiperEl.querySelector('.js-carousel-link');
        if (linkEl && slideLinkUrl && slideLinkLabel) {
          linkEl.setAttribute('href', slideLinkUrl);
          linkEl.textContent = slideLinkLabel;
        }
      };
    }

    // Initialize swiper
    try {
      const swiper = new Swiper(carousel, swiperConfig);

      // Additional pause on hover functionality for better UX
      if (autoplay && pauseOnHover) {
        carousel.addEventListener('mouseenter', () => {
          if (swiper.autoplay && swiper.autoplay.running) {
            swiper.autoplay.stop();
          }
        });

        carousel.addEventListener('mouseleave', () => {
          if (swiper.autoplay && !swiper.autoplay.running) {
            swiper.autoplay.start();
          }
        });
      }
    } catch (error) {
      // Silently handle errors in production
    }
  }
}
